import { useEffect, useState } from "react";
import { Crown, Timer } from "lucide-react";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const countdown = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "Settling…";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return `${d}d ${h}h ${m}m`;
};

const MEDAL = ["#FFC107", "#C0C0C0", "#CD7F32"];

export default function RacePanel() {
  const { user } = useAuth();
  const [race, setRace] = useState(null);
  const [history, setHistory] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/race").then((r) => setRace(r.data)).catch(() => {});
    api.get("/race/history?limit=3").then((r) => setHistory(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(t); }, []);

  if (!race) return <div className="panel p-8 text-sm text-zinc-500" data-testid="race-loading">Loading race…</div>;

  return (
    <div className="flex flex-col gap-5" data-testid="race-panel">
      <div className="panel p-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(600px 200px at 0% 0%, rgba(255,193,7,0.12), transparent)" }} />
        <div className="relative">
          <div className="chip w-fit mb-3" style={{ color: "#FFC107", borderColor: "rgba(255,193,7,0.3)" }}>Weekly Race · week of {race.week}</div>
          <div className="font-display text-3xl md:text-4xl font-black tracking-tighter">
            <span className="gold-text" data-testid="race-pool">{formatAmount(race.pool, "FMI")}</span> <span className="text-zinc-400 text-xl">FMI pool</span>
          </div>
          <div className="text-sm text-zinc-500 mt-2">Top 10 FMI wagerers split the pool every Monday 00:00 UTC. Every FMI bet counts.</div>
        </div>
        <div className="relative panel px-5 py-4 text-center" style={{ background: "#0F0F11" }}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 justify-center"><Timer size={11} /> Ends in</div>
          <div className="font-mono-num text-xl font-bold mt-1" data-testid="race-countdown" key={tick}>{countdown(race.ends_at)}</div>
        </div>
      </div>

      {race.me && (
        <div className="panel px-5 py-3 flex items-center gap-4 text-sm" data-testid="race-me">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Your position</span>
          <span className="font-mono-num font-bold text-amber-400">#{race.me.rank}</span>
          <span className="font-mono-num text-zinc-300">{formatAmount(race.me.wagered, "FMI")} FMI wagered</span>
          <span className="ml-auto font-mono-num text-emerald-400">{race.me.prize > 0 ? `+${formatAmount(race.me.prize, "FMI")} FMI` : "Not in prizes yet"}</span>
        </div>
      )}

      <div className="panel overflow-hidden overflow-x-auto">
        <table className="data-table" data-testid="race-table">
          <thead>
            <tr><th style={{ width: 60 }}>#</th><th>Player</th><th>Bets</th><th>Wagered</th><th>Prize</th></tr>
          </thead>
          <tbody>
            {race.standings.length === 0 ? (
              <tr><td colSpan={5} className="text-zinc-600 text-center py-12">No FMI bets this week yet. The podium is empty.</td></tr>
            ) : (
              race.standings.map((r) => (
                <tr key={r.user_id} data-testid={`race-row-${r.rank}`} className={user?.id === r.user_id ? "bg-amber-500/5" : ""}>
                  <td className="font-mono-num font-bold" style={{ color: MEDAL[r.rank - 1] || "#71717A" }}>
                    {r.rank <= 3 ? <Crown size={14} className="inline mr-1 -mt-0.5" /> : null}#{r.rank}
                  </td>
                  <td>{r.username}</td>
                  <td className="font-mono-num">{r.bets}</td>
                  <td className="font-mono-num">{formatAmount(r.wagered, "FMI")}</td>
                  <td className="font-mono-num text-emerald-400 font-semibold">+{formatAmount(r.prize, "FMI")}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-5 md:grid-cols-10 gap-1 text-center" data-testid="race-split">
        {race.split.map((p, i) => (
          <div key={i} className="panel py-2">
            <div className="text-[9px] text-zinc-500 uppercase">#{i + 1}</div>
            <div className="font-mono-num text-xs" style={{ color: MEDAL[i] || "#D4D4D8" }}>{Math.round(p * 100)}%</div>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <section data-testid="race-history">
          <h3 className="font-display text-lg font-black tracking-tighter mb-3">Past champions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {history.map((h) => (
              <div key={h.week} className="panel p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Week of {h.week}</div>
                <div className="font-display font-black text-lg mt-1 truncate">{h.winners?.[0]?.username || "—"}</div>
                <div className="text-xs text-zinc-500 font-mono-num">{formatAmount(h.total_paid, "FMI")} FMI paid to {h.winners?.length || 0} players</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
